/**
 * AI Streaming utility using fetch + ReadableStream + SSE
 * axios doesn't support streaming, so we use native fetch
 */

/**
 * @param {string} url - Full API endpoint URL
 * @param {FormData} formData - Form data to POST
 * @param {Object} callbacks - { onMeta, onThinking, onText, onDone, onError }
 * @param {Object} options - { timeout: 60000 }
 * @returns {AbortController} - Call .abort() to cancel
 */
export function streamAIResponse (url, formData, callbacks = {}, options = {}) {
  const controller = new AbortController()
  const timeout = options.timeout || 120000

  // Add stream=1 to formData
  formData.append('stream', '1')

  const timeoutId = setTimeout(() => controller.abort(), timeout)

  fetch(url, {
    method: 'POST',
    body: formData,
    signal: controller.signal,
    credentials: 'same-origin'
  })
    .then(response => {
      clearTimeout(timeoutId)

      // Non-2xx responses are errors (usage limit, not registered, etc.)
      if (!response.ok) {
        return response.json().then(data => {
          if (callbacks.onError) callbacks.onError(data)
          throw new Error(data.message || 'Request failed')
        })
      }

      // Check if it's actually a stream
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('text/event-stream')) {
        // Fallback: treat as JSON response
        return response.json().then(data => {
          if (data.success && callbacks.onDone) {
            callbacks.onDone({ full_text: data.message })
          } else if (callbacks.onError) {
            callbacks.onError(data)
          }
        })
      }

      // Parse SSE stream
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      function handleSSEMessage (message) {
        if (!message.trim()) return

        let eventType = 'message'
        let data = ''

        for (const line of message.split('\n')) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7)
          } else if (line.startsWith('data: ')) {
            data = line.slice(6)
          }
        }

        if (!data) return

        try {
          const parsed = JSON.parse(data)

          switch (eventType) {
            case 'meta':
              if (callbacks.onMeta) callbacks.onMeta(parsed)
              break
            case 'thinking':
              if (callbacks.onThinking) callbacks.onThinking()
              break
            case 'text':
              if (callbacks.onText) callbacks.onText(parsed.content)
              break
            case 'reasoning':
              if (callbacks.onReasoning) callbacks.onReasoning(parsed.content)
              break
            case 'done':
              if (callbacks.onDone) callbacks.onDone(parsed)
              break
            case 'error':
              if (callbacks.onError) callbacks.onError(parsed)
              break
          }
        } catch (e) {
          // Ignore parse errors
        }
      }

      function processChunk () {
        return reader.read().then(({ done, value }) => {
          if (done) {
            // ストリーム終了時、バッファに残ったデータを処理
            if (buffer.trim()) {
              for (const msg of buffer.split('\n\n')) {
                handleSSEMessage(msg)
              }
            }
            return
          }

          buffer += decoder.decode(value, { stream: true })

          // Process complete SSE messages (separated by \n\n)
          const messages = buffer.split('\n\n')
          buffer = messages.pop() // Keep incomplete message in buffer

          for (const message of messages) {
            handleSSEMessage(message)
          }

          return processChunk()
        })
      }

      return processChunk()
    })
    .catch(err => {
      clearTimeout(timeoutId)
      if (err.name === 'AbortError') {
        // Timeout or manual cancel - ignore
        return
      }
      if (callbacks.onError) {
        callbacks.onError({ message: err.message || 'ネットワークエラーが発生しました。' })
      }
    })

  return controller
}
